import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router';

const LAST_UPDATED_DATE = 'December 14, 2025';

/** Shared ToS body — used by the /terms page and the signup wizard preview box. */
export const TermsContent = () => {
  return (
    <div className="space-y-6 text-sm/6 text-grey-400 text-left">
      <p>Last updated: {LAST_UPDATED_DATE}</p>

      {/* Section 1 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          1. Parties; Acceptance; Electronic Signature
        </h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            These Master Terms form a legally binding agreement between Marketplace AI, Inc.
            ("Company," "we," "us," or "our") and you ("User," "you," or "your"). By creating an
            account, accessing, or using any Marketplace AI Product, you agree to these Master Terms
            and all documents incorporated by reference.
          </p>
          <p>
            <strong>Electronic Signature.</strong> You agree that clicking an "I Agree" or similar
            button, checking a box, or taking any other affirmative action presented as acceptance
            constitutes your electronic signature and your intent to be bound, with the same legal
            effect as a handwritten signature (subject to applicable law).
          </p>
        </div>
      </div>

      {/* Section 2 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          2. Integrated Platform and Covered Products
        </h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            Marketplace AI operates multiple products (each a "Product") under one corporate
            umbrella and a common legal framework. These Master Terms apply across all Products,
            whether accessed via separate URLs, mobile applications, or interfaces.
          </p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>HealthcareLX (marketplace, matching, negotiation, and hiring workflows).</li>
            <li>
              MedCredVault (credential capture, storage, verification workflows, and credential
              analytics).
            </li>
            <li>
              Any additional Marketplace AI offerings, integrations, beta, pilot, or successor
              services we may provide.
            </li>
          </ul>
          <p>
            <strong>Module Addenda.</strong> Some Products have special terms (e.g., credentialing
            data, healthcare privacy obligations). Those product-specific terms appear in Module
            Addenda. If an Addendum conflicts with these Master Terms, the Addendum controls only
            for that Product and only to the extent of the conflict.
          </p>
        </div>
      </div>

      {/* Section 3 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          3. Incorporated Documents; Order of Precedence
        </h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            The following documents are incorporated by reference and are part of the agreement
            (collectively, "Governing Documents"). Order of precedence (highest to lowest):
          </p>
          <ol className="list-decimal pl-5 mt-1 space-y-1">
            <li>
              Fee Schedule / Order Forms / Subscription Terms (as presented at checkout or
              contracting).
            </li>
            <li>Unblinding &amp; Non-Circumvention Agreement (Schedule A).</li>
            <li>Privacy Policy (including Cookie Policy, where applicable).</li>
            <li>Acceptable Use Policy (AUP).</li>
            <li>
              Any Business Associate Agreement (BAA) executed between Company and a Covered
              Entity/Business Associate (if applicable).
            </li>
            <li>Module Addenda (Schedule B).</li>
          </ol>
        </div>
      </div>

      {/* Section 4 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          4. Eligibility; User Types; Authority
        </h3>
        <div className="mt-2 text-grey-400">
          <p>You represent and warrant that you:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Are at least 18 years old and legally able to enter into binding contracts.</li>
            <li>
              If you are acting for an entity (e.g., hospital, group, agency), you have authority to
              bind that entity.
            </li>
            <li>
              Will comply with all laws applicable to your use, including healthcare staffing,
              credentialing, anti-discrimination, and privacy laws.
            </li>
          </ul>
        </div>
      </div>

      {/* Section 5 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          5. Accounts; Security; Administrators
        </h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            You must provide accurate, current information and keep it updated. You are responsible
            for safeguarding credentials and for all activity under your account. You must promptly
            notify us of suspected unauthorized access.
          </p>
          <p>
            <strong>Organization Accounts.</strong> If your organization uses administrative users,
            you are responsible for proper role assignment, least-privilege access, and internal
            user offboarding.
          </p>
        </div>
      </div>

      {/* Section 6 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          6. Marketplace Conduct: No Circumvention; No Exclusivity
        </h3>
        <div className="mt-2 text-grey-400 space-y-4">
          <div>
            <h4 className="font-semibold text-grey-100">
              6.1 No Fee Circumvention (Non-Circumvention)
            </h4>
            <p className="mt-1">
              You may not use the Platform to identify, source, evaluate, or connect with another
              User and then complete a transaction or relationship off-platform for the purpose of
              avoiding platform fees or obligations.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-grey-100">
              6.2 No Exclusivity / Non-Compete Requirements
            </h4>
            <p className="mt-1">
              You may not request, require, or enforce exclusivity, non-compete, or similar
              restrictions that prevent Users from working with others, whether or not they met on
              the Platform. Any such provision introduced in connection with the Platform is null
              and void as between the Users to the fullest extent permitted by law, and is a
              material breach of these Master Terms.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-grey-100">6.3 Remedies</h4>
            <p className="mt-1">
              If we determine (in our reasonable discretion) that a User has attempted to circumvent
              fees or impose prohibited restrictions, we may suspend or terminate accounts, pursue
              payment of owed fees, and seek equitable and monetary relief.
            </p>
          </div>
        </div>
      </div>

      {/* Section 7 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          7. Unblinding and Mutual Consent Contact Exchange
        </h3>
        <p className="mt-2 text-grey-400">
          Certain workflows are "blinded" to protect Users and preserve marketplace integrity. The
          Unblinding &amp; Non-Circumvention Agreement in Schedule A governs: (i) how one party
          requests unblinding, (ii) the counterparty's right to accept or decline, and (iii) the
          obligations that attach if both parties affirmatively accept.
        </p>
      </div>

      {/* Section 8 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">8. Fees; Billing; Taxes</h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            Fees may include subscriptions, postings, usage-based fees, placement fees, or other
            fees described in an Order Form or Fee Schedule. Taxes are additional where applicable.
            You are responsible for all taxes owed by you arising from your activities on the
            Platform.
          </p>
          <p>
            If a fee is owed due to an accepted unblinding or other triggering event (as defined in
            Schedule A or an Order Form), you authorize Company to invoice you and/or charge a
            payment method on file, subject to applicable law and the payment terms presented at the
            time.
          </p>
        </div>
      </div>

      {/* Section 9 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">9. Data; Privacy; Security</h3>
        <div className="mt-2 text-grey-400 space-y-4">
          <div>
            <h4 className="font-semibold text-grey-100">9.1 Data Use Across Products</h4>
            <p className="mt-1">
              Because Products are integrated, data you provide in one Product may be used within
              other Products to provide services (e.g., credential data enabling faster job
              applications; operational telemetry improving system reliability).
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-grey-100">9.2 Security; Audit Logs</h4>
            <p className="mt-1">
              We use administrative, technical, and physical safeguards consistent with industry
              practice. You acknowledge that no system is perfectly secure. For enforcement and
              safety, we may maintain audit logs (including click-acceptance records, timestamps,
              and event IDs).
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-grey-100">9.3 HIPAA / Health Data (If Applicable)</h4>
            <p className="mt-1">
              If you are a Covered Entity or Business Associate under HIPAA and your use involves
              Protected Health Information (PHI), a Business Associate Agreement (BAA) may be
              required before PHI is processed. If no BAA is in place, you agree not to upload PHI
              except as expressly permitted by law and our written agreement.
            </p>
          </div>
        </div>
      </div>

      {/* Section 10 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          10. Intellectual Property; License; Feedback
        </h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            Company retains all rights in the Products and Platform materials. Subject to your
            compliance, we grant you a limited, revocable, non-exclusive, non-transferable license
            to access and use the Products.
          </p>
          <p>
            <strong>Feedback.</strong> If you provide feedback or suggestions, you grant Company the
            right to use it without restriction or compensation.
          </p>
        </div>
      </div>

      {/* Section 11 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          11. Third-Party Services; Integrations
        </h3>
        <p className="mt-2 text-grey-400">
          The Platform may integrate with third-party services (e.g., credential verification
          sources, background check vendors, EHR/PACS systems, payment processors). Third-party
          terms may apply. Company is not responsible for third-party services not controlled by
          Company.
        </p>
      </div>

      {/* Section 12 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">12. Disclaimers</h3>
        <ul className="list-disc pl-5 mt-2 text-grey-400 space-y-1">
          <li>
            <strong>No Medical Advice.</strong> The Platform does not provide medical advice or
            clinical decision-making.
          </li>
          <li>
            <strong>No Employment Agency.</strong> Unless expressly stated in an Order Form, Company
            is not a staffing agency, employer, or agent of any User.
          </li>
          <li>
            <strong>As-Is.</strong> The Platform is provided "AS IS" and "AS AVAILABLE" without
            warranties to the fullest extent permitted by law.
          </li>
        </ul>
      </div>

      {/* Section 13 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">13. Limitation of Liability</h3>
        <p className="mt-2 text-grey-400">
          To the fullest extent permitted by law, Company will not be liable for indirect,
          incidental, special, consequential, exemplary, or punitive damages, or for loss of
          profits, revenues, data, or goodwill. Company's total liability for any claim will not
          exceed the amounts paid by you to Company for the Product giving rise to the claim in the
          six (6) months preceding the event, or $1,000, whichever is greater, unless an Order Form
          states otherwise.
        </p>
      </div>

      {/* Section 14 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">14. Indemnification</h3>
        <p className="mt-2 text-grey-400">
          You agree to defend, indemnify, and hold harmless Company and its affiliates, officers,
          directors, employees, and agents from claims, damages, liabilities, and expenses
          (including reasonable attorneys' fees) arising from: (i) your use of the Platform; (ii)
          your content/data; (iii) your breach of these Governing Documents; or (iv) your violation
          of law or third-party rights.
        </p>
      </div>

      {/* Section 15 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">
          15. Dispute Resolution; Company Election of Forum
        </h3>
        <p className="mt-2 text-grey-400">
          <strong>Company Election.</strong> To the maximum extent permitted by law, Company may
          elect, in its sole discretion, that any dispute or claim arising from or relating to the
          Platform be resolved either (a) in court (litigation) or (b) by binding arbitration.
          Company's election may be made by written notice at any time before a final adjudication.
          If Company elects arbitration, the arbitration will be administered by a reputable
          provider (e.g., AAA/JAMS) under commercial rules, with the seat and venue set forth below,
          and judgment on the award may be entered in any court of competent jurisdiction.
        </p>
      </div>

      {/* Section 16 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">16. Governing Law; Venue</h3>
        <div className="mt-2 text-grey-400 space-y-2">
          <p>
            <strong>Governing Law.</strong> This Agreement and any dispute or claim arising out of
            or relating to the Platform or these Governing Documents shall be governed by and
            construed in accordance with the laws of the State of Delaware, without regard to
            conflict-of-laws principles.
          </p>
          <p>
            <strong>Venue Selection Controlled by Company.</strong> To the maximum extent permitted
            by law, Company shall have the sole and exclusive right to designate the venue for any
            litigation, arbitration, mediation, or other dispute-resolution proceeding arising out
            of or relating to this Agreement, the Platform, or any Governing Document.
          </p>
          <p>Such venue may be:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Any state or federal court of competent jurisdiction selected by Company; or</li>
            <li>
              If arbitration is elected by Company, the arbitral seat and hearing location
              designated by Company.
            </li>
          </ul>
          <p>
            Each User irrevocably waives any objection to venue, forum non conveniens, or
            inconvenience of forum with respect to any venue selected by Company.
          </p>
          <p>
            <strong>Injunctive and Equitable Relief (Carve-Out).</strong> Notwithstanding any
            election of arbitration or other dispute-resolution mechanism, Company may at any time
            seek temporary, preliminary, or permanent injunctive relief, specific performance, or
            other equitable relief in any court of competent jurisdiction selected by Company to
            prevent or restrain actual or threatened breaches of this Agreement, including without
            limitation breaches relating to intellectual property, confidentiality, data misuse,
            security, non-circumvention, or unauthorized access to the Platform.
          </p>
          <p>
            <strong>Emergency Relief.</strong> Company's right to seek emergency or equitable relief
            shall not be deemed incompatible with, or a waiver of, any agreement to arbitrate or
            other dispute-resolution provisions, and such relief may be sought without first
            engaging in informal resolution, mediation, or arbitration procedures.
          </p>
        </div>
      </div>

      {/* Section 17 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">17. Termination; Suspension</h3>
        <p className="mt-2 text-grey-400">
          We may suspend or terminate access for violations of the Governing Documents, security
          risk, suspected fraud, or as needed to protect Users and the Platform. Upon termination,
          your license ends and your access ceases. Certain provisions survive termination
          (including fees owed, limitation of liability, indemnification, dispute resolution, and
          audit rights).
        </p>
      </div>

      {/* Section 18 */}
      <div>
        <h3 className="text-xl font-semibold text-grey-100">18. Miscellaneous</h3>
        <ul className="list-disc pl-5 mt-2 text-grey-400 space-y-1">
          <li>
            <strong>Assignment:</strong> Company may assign these terms; you may not assign without
            Company's consent.
          </li>
          <li>
            <strong>Severability:</strong> If any provision is unenforceable, remaining provisions
            remain effective.
          </li>
          <li>
            <strong>Waiver:</strong> Failure to enforce is not a waiver.
          </li>
          <li>
            <strong>Notices:</strong> Notices may be provided via email, in-product notice, or
            posting on the Platform.
          </li>
        </ul>
      </div>
    </div>
  );
};

export const Terms = () => {
  return (
    <div className="text-left bg-white py-12 sm:py-16 flex-grow">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-50 hover:text-primary-t-100 mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back to Home
        </Link>
        <h1 className="text-2xl font-semibold text-grey-100 sm:text-3xl">Terms of Service</h1>
        <div className="mt-8">
          <TermsContent />
        </div>
      </div>
    </div>
  );
};
